"use client";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface ArticleBodyProps {
  content: string;
}

export default function ArticleBody({ content }: ArticleBodyProps) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: () => null,
        h2: () => null,
        h3: ({ children }) => (
          <h3 className="text-base lg:text-lg font-black uppercase leading-none mb-4 mt-10 tracking-tight">
            {children}
          </h3>
        ),
        p: ({ children }) => (
          <p className="text-base lg:text-lg leading-relaxed mb-6 max-w-[65ch]">
            {children}
          </p>
        ),
        ul: ({ children }) => (
          <ul className="list-disc pl-6 mb-6 space-y-2 max-w-[65ch]">
            {children}
          </ul>
        ),
        ol: ({ children }) => (
          <ol className="list-decimal pl-6 mb-6 space-y-2 max-w-[65ch]">
            {children}
          </ol>
        ),
        li: ({ children }) => (
          <li className="text-base lg:text-lg leading-relaxed">{children}</li>
        ),
        blockquote: ({ children }) => (
          <blockquote className="border-l-2 border-foreground/30 pl-6 italic mb-6 text-gray max-w-[65ch]">
            {children}
          </blockquote>
        ),
        code: ({ className, children }) => {
          const isInline = !className;
          if (isInline) {
            return (
              <code className="bg-foreground/10 px-1.5 py-0.5 rounded text-sm font-mono">
                {children}
              </code>
            );
          }
          return (
            <pre className="bg-foreground/5 border border-foreground/10 p-4 overflow-x-auto mb-6 rounded-none">
              <code className="text-sm font-mono leading-relaxed block">
                {children}
              </code>
            </pre>
          );
        },
        a: ({ href, children }) => (
          <a
            href={href}
            className="underline underline-offset-4 hover:text-gray transition-colors duration-300"
            target={href?.startsWith("http") ? "_blank" : undefined}
            rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
          >
            {children}
          </a>
        ),
        hr: () => <hr className="border-foreground/20 my-12" />,
      }}
    >
      {content}
    </ReactMarkdown>
  );
}
